import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, MenuItem, Stack, TextField } from '@mui/material';
import { Assumption, energyProjectTypes, newAssumption, transmissionProjectTypes } from '../../TableData';
import { integerRegex } from '../../componentsConfig/utils';

interface Props {
    onClose: () => void,
    onSubmit: (values: Assumption) => void,
    open: boolean,
    AllLists: any
}

const CreateNewAssumptionModal = ({ open,
    onClose,
    onSubmit,
    AllLists

}: Props) => {
    // state
    const [values, setValues] = useState<Assumption>({
        RawProjectType: "",
        PlanningTimePercentage: 0,
        ConstructionTimePercentage: 0,
        CommissioningTimePercentage: 0,
        PlanningCostPercentage: 0,
        ConstructionCostPercentage: 0,
        CommissioningCostPercentage: 0
    });
    const [isAlertOpen, setIsAlertOpen] = useState(false);

    const timeTotal = Number(values.PlanningTimePercentage) + Number(values.ConstructionTimePercentage) + Number(values.CommissioningTimePercentage);
    const investmentTotal = Number(values.PlanningCostPercentage) + Number(values.ConstructionCostPercentage) + Number(values.CommissioningCostPercentage);

    const validateTimeInvestmentTotal = () => {
        if (timeTotal !== 100) {
            return "Time allocation must sum to 100%"
        }
        if (investmentTotal !== 100) {
            return "Investment allocation must sum to 100%"
        }
        return ""
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.name === "RawProjectType") {
            setValues({ ...values, [e.target.name]: e.target.value })
        } else {
            // Regular expression to match valid number with up to one decimal place

            if (integerRegex.test(e.target.value) && e.target.value.indexOf(".") === -1 && e.target.value.length <= 3) {
                setValues({ ...values, [e.target.name]: e.target.value })
            }
        }
    }


    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        if (!validateTimeInvestmentTotal()) {
            AllLists?.executeGetRawProjectTypes.forEach((type) => {
                if (type.RawProjectType === values.RawProjectType) {
                    onSubmit({ ...values, ProjectTypeID: type.RawProjectTypeID })
                }
            })
            setValues({ ...newAssumption }) // clear the form
            setIsAlertOpen(false)
            onClose()
        } else {
            setIsAlertOpen(true)
        }
    }

    useEffect(() => {
        if (investmentTotal === 100 && timeTotal === 100) {
            setIsAlertOpen(false)
        }
    }, [investmentTotal, timeTotal])



    return (
        <Dialog open={open}
            fullWidth={true}
            maxWidth="lg">
            <form onSubmit={handleSubmit}>
                <DialogTitle id="create-dialog-title">Create New Assumption</DialogTitle>
                <DialogContent>
                    <Box p={2} className="hide-asterisk">
                        <Grid container spacing={2}>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    key="RawProjectType"
                                    name="RawProjectType"
                                    label="Project type"
                                    select
                                    value={values.RawProjectType}
                                    required
                                    onChange={handleChange}
                                >
                                    {AllLists?.executeGetRawProjectTypes.map((option) => {
                                        if ([...energyProjectTypes, ...transmissionProjectTypes].indexOf(option.RawProjectType) === -1) {
                                            return <MenuItem key={option.RawProjectType} value={option.RawProjectType}>
                                                {option.RawProjectType}
                                            </MenuItem>
                                        }
                                    })}
                                </TextField>
                            </Grid>
                        </Grid>
                    </Box>
                    <DialogTitle id="time-allocation-title">Time allocation</DialogTitle>
                    <Box p={2} className="hide-asterisk">
                        <Grid container spacing={2}>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    key="PlanningTimePercentage"
                                    name='PlanningTimePercentage'
                                    label="Planning Percentage"
                                    required
                                    type='number'
                                    value={values.PlanningTimePercentage}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    key="ConstructionTimePercentage"
                                    name='ConstructionTimePercentage'
                                    label="Construction Percentage"
                                    required
                                    type='number'
                                    value={values.ConstructionTimePercentage}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    key="CommissioningTimePercentage"
                                    name='CommissioningTimePercentage'
                                    label="Commissioning Percentage"
                                    required
                                    value={values.CommissioningTimePercentage}
                                    type='number'
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    key="TimeTotal"
                                    label="Total"
                                    required
                                    error={isNaN(timeTotal) || timeTotal !== 100}
                                    value={timeTotal + "%"}
                                    disabled
                                />
                            </Grid>
                        </Grid>
                    </Box>
                    <DialogTitle id="investment-allocation-title">Investment allocation</DialogTitle>
                    <Box p={2} className="hide-asterisk">
                        <Grid container spacing={2}>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    key="PlanningCostPercentage"
                                    name='PlanningCostPercentage'
                                    label="Planning Percentage"
                                    type='number'
                                    required
                                    value={values.PlanningCostPercentage}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    key="ConstructionCostPercentage"
                                    name='ConstructionCostPercentage'
                                    label="Construction Percentage"
                                    type='number'
                                    required
                                    value={values.ConstructionCostPercentage}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    key="CommissioningCostPercentage"
                                    name='CommissioningCostPercentage'
                                    label="Commissioning Percentage"
                                    type='number'
                                    required
                                    value={values.CommissioningCostPercentage}
                                    onChange={handleChange}
                                />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField
                                    fullWidth
                                    key="CostTotal"
                                    label="Total"
                                    required
                                    disabled
                                    error={isNaN(investmentTotal) || investmentTotal !== 100}
                                    value={investmentTotal + "%"}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </DialogContent>
                {isAlertOpen && <Alert severity="error">{validateTimeInvestmentTotal()}</Alert>}
                <DialogActions sx={{ p: "1.25rem" }}>
                    <Button onClick={() => {
                        setValues({ ...newAssumption }) // clear the form
                        setIsAlertOpen(false)
                        onClose();
                    }}>Cancel</Button>
                    <Button color="secondary" variant="contained" type="submit">
                        Create
                    </Button>
                </DialogActions>
            </form>
        </Dialog >
    );
}

export default CreateNewAssumptionModal;